import React, { useState } from 'react';
import { Crown, CheckCircle2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { currentUser } from '../mock/mockData';
import { toast } from '../hooks/use-toast';
import RazorpayCheckout from '../components/RazorpayCheckout';
import { subscriptionApi } from '../lib/api';

export default function Subscription() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const sub = currentUser.subscription;
  const priceExcl = 500;

  const onSuccess = async (result) => {
    try { await subscriptionApi.upgrade(result.method); } catch (e) {}
    toast({ title: 'Subscription Active!', description: `Pro plan activated. Payment ID: ${result.payment_id}. GST invoice sent to email.` });
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Subscription</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your EZLOAD Pro subscription.</p>
      </div>

      <div className="bg-gradient-to-br from-[#0B2545] to-[#13315C] rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#00A651]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#FFB800]/10 rounded-full blur-2xl"></div>
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <Badge className="bg-[#FFB800] text-[#0B2545] border-0 mb-3"><Crown className="w-3 h-3 mr-1" />CURRENT PLAN</Badge>
            <h2 className="text-3xl font-extrabold font-[Poppins] mb-1">{sub.plan}</h2>
            <p className="text-white/70">Renews on {sub.renewsOn} • {sub.daysLeft} days remaining</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge className="bg-white/10 border-0 text-white">Load Board ✓</Badge>
              <Badge className="bg-white/10 border-0 text-white">Truck Board ✓</Badge>
              <Badge className="bg-white/10 border-0 text-white">10 e-LR/month ✓</Badge>
              <Badge className="bg-white/10 border-0 text-white">UPI Payments ✓</Badge>
            </div>
          </div>
          <Button onClick={() => setCheckoutOpen(true)} className="bg-[#00A651] hover:bg-[#008a43] text-white">Upgrade to Pro</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <p className="text-sm font-semibold text-gray-500">FREE TRIAL</p>
          <div className="mt-2 mb-4 flex items-baseline gap-2">
            <span className="text-4xl font-extrabold text-[#0B2545] font-[Poppins]">₹0</span>
            <span className="text-gray-500">/first month</span>
          </div>
          <ul className="space-y-2 mb-6">
            {['Load Board access', 'Truck Board & lane match', 'Basic rate insights', 'Up to 10 e-LR/month', 'UPI & COD payments'].map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-[#00A651] flex-shrink-0 mt-0.5" /><span>{f}</span></li>
            ))}
          </ul>
          <Button variant="outline" className="w-full" disabled>Currently Active</Button>
        </div>

        <div className="bg-white border-2 border-[#00A651] rounded-2xl p-6 relative">
          <Badge className="absolute -top-3 left-6 bg-[#00A651] text-white border-0">RECOMMENDED</Badge>
          <p className="text-sm font-semibold text-[#00A651]">PRO</p>
          <div className="mt-2 mb-1 flex items-baseline gap-2">
            <span className="text-4xl font-extrabold text-[#0B2545] font-[Poppins]">₹500</span>
            <span className="text-gray-500">+ GST /month</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">₹590 total including 18% GST</p>
          <ul className="space-y-2 mb-6">
            {['Unlimited Load & Truck Board', 'Advanced lane rate insights', 'Unlimited e-LR & e-Way Bills', 'GST-compliant invoicing', 'UPI, RTGS, NEFT, COD', 'Priority verified partner access', '24x7 phone & WhatsApp support'].map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-[#00A651] flex-shrink-0 mt-0.5" /><span>{f}</span></li>
            ))}
          </ul>
          <Button onClick={() => setCheckoutOpen(true)} className="w-full bg-[#00A651] hover:bg-[#008a43] text-white">Upgrade Now</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h2 className="text-lg font-bold text-[#0B2545] mb-4">Billing History</h2>
        <div className="text-center py-10 text-gray-500 text-sm">
          <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          No invoices yet. Your first invoice will be generated when your trial ends.
        </div>
      </div>

      <RazorpayCheckout open={checkoutOpen} onClose={() => setCheckoutOpen(false)} amount={priceExcl} description="EZLOAD Pro — 1 Month Subscription" onSuccess={onSuccess} />
    </div>
  );
}
